import { css } from '@emotion/core';
import { Theme } from '@material-ui/core/styles';

import { zIndexes } from '../../common/css-variables';

export const DEFAULT_SIDEBAR_WIDTH = 520;
export const MIN_SIDEBAR_WIDTH = 460;
export const MAX_SIDEBAR_WIDTH = 1200;
export const TOP_SPACING = 80;
export const SIDE_SPACING = 24;
export const DEFAULT_SIDEBAR_HEIGHT = 0; // 0 means auto/flex-grow to bottom
export const MIN_SIDEBAR_HEIGHT = 400;

export type ResizeEdge =
    | 'top'
    | 'right'
    | 'bottom'
    | 'left'
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right';

export const createStyles = (
    theme: Theme,
    open: boolean,
    isDocked: boolean,
    dockSide: 'left' | 'right',
    width: number,
    height: number,
    position: { x: number; y: number }
) => {
    const isRight = dockSide === 'right';
    const translateClosed = isRight ? 'translateX(100%)' : 'translateX(-100%)';

    let positioningStyles: string;

    if (isDocked) {
        // Docked mode: anchored to bottom with SIDE_SPACING margin
        // When height is 0 (default), fill all available vertical space (viewport - TOP_SPACING - SIDE_SPACING)
        // When height > 0, use that specific custom height but still anchored to bottom
        const heightStyle = height > 0 ? `height: ${height}px;` : `top: ${TOP_SPACING}px;`;
        positioningStyles = `
            bottom: ${SIDE_SPACING}px;
            ${isRight ? `right: ${SIDE_SPACING}px;` : `left: ${SIDE_SPACING}px;`}
            ${heightStyle}
        `;
    } else {
        // Floating mode: absolute positioning with explicit dimensions
        positioningStyles = `
            top: ${position.y}px;
            left: ${position.x}px;
            height: ${height}px;
        `;
    }

    return css`
        position: fixed;
        ${positioningStyles}
        width: ${width}px;
        max-width: min(${MAX_SIDEBAR_WIDTH}px, calc(100vw - ${SIDE_SPACING * 2}px));
        max-height: calc(100vh - ${TOP_SPACING}px - ${SIDE_SPACING}px);
        display: flex;
        flex-direction: column;
        background: ${theme.palette.background.paper};
        box-shadow: 0 20px 60px rgba(15, 23, 42, 0.4);
        border-radius: 16px;
        z-index: ${zIndexes.orbitOverlay};
        overflow: hidden;
        pointer-events: ${open ? 'auto' : 'none'};
        visibility: ${open ? 'visible' : 'hidden'};
        opacity: ${open ? 1 : 0};
        transform: ${open ? 'scale(1)' : `${isDocked ? translateClosed : ''} scale(0.96)`};
        transition:
            transform 0.3s cubic-bezier(0.32, 0.72, 0, 1),
            opacity 0.3s cubic-bezier(0.32, 0.72, 0, 1),
            width 0s,
            height 0s,
            top 0s,
            left 0s,
            visibility 0s linear ${open ? '0s' : '0.3s'};
    `;
};

export const contentStyles = css`
    flex: 1 1 auto;
    overflow: hidden;
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    /* height: 100%; */
`;

export const resizeHandleWidthStyles = (dockSide: 'left' | 'right') => css`
    position: absolute;
    top: 10px; /* Leave space for corner/top handles */
    bottom: 0;
    ${dockSide === 'right' ? 'left: -6px;' : 'right: -6px;'}
    width: 12px;
    cursor: col-resize;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s;

    &:hover,
    &[data-resizing='true'] {
        opacity: 1;
    }

    &::after {
        content: '';
        width: 4px;
        height: 40px;
        background-color: rgba(0, 0, 0, 0.2);
        border-radius: 2px;
    }
`;

export const resizeHandleHeightStyles = css`
    position: absolute;
    top: -6px;
    left: 0;
    right: 0;
    height: 12px;
    cursor: ns-resize;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s;

    &:hover,
    &[data-resizing='true'] {
        opacity: 1;
    }

    &::after {
        content: '';
        height: 4px;
        width: 40px;
        background-color: rgba(0, 0, 0, 0.2);
        border-radius: 2px;
    }
`;

export const floatingResizeHandleStyles = (edge: ResizeEdge) => {
    const isVertical = edge === 'top' || edge === 'bottom';
    const cursor = isVertical ? 'ns-resize' : 'ew-resize';
    const positionStyle =
        edge === 'top'
            ? 'top: -6px; left: 10px; right: 10px; height: 12px;'
            : edge === 'bottom'
              ? 'bottom: -6px; left: 10px; right: 10px; height: 12px;'
              : edge === 'left'
                ? 'left: -6px; top: 10px; bottom: 10px; width: 12px;'
                : 'right: -6px; top: 10px; bottom: 10px; width: 12px;';

    return css`
        position: absolute;
        ${positionStyle}
        cursor: ${cursor};
        z-index: 100;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.2s;

        &:hover,
        &[data-resizing='true'] {
            opacity: 1;
        }

        &::after {
            content: '';
            ${isVertical ? 'height: 4px; width: 40px;' : 'width: 4px; height: 40px;'}
            background-color: rgba(0, 0, 0, 0.2);
            border-radius: 2px;
        }
    `;
};

export const floatingResizeCornerStyles = (corner: ResizeEdge) => {
    const cursor = corner === 'top-left' || corner === 'bottom-right' ? 'nwse-resize' : 'nesw-resize';
    const positionStyle =
        corner === 'top-left'
            ? 'top: -6px; left: -6px;'
            : corner === 'top-right'
              ? 'top: -6px; right: -6px;'
              : corner === 'bottom-left'
                ? 'bottom: -6px; left: -6px;'
                : 'bottom: -6px; right: -6px;';

    return css`
        position: absolute;
        ${positionStyle}
        width: 12px;
        height: 12px;
        cursor: ${cursor};
        z-index: 101;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.2s;

        &:hover,
        &[data-resizing='true'] {
            opacity: 1;
        }

        &::after {
            content: '';
            width: 6px;
            height: 6px;
            background-color: rgba(0, 0, 0, 0.3);
            border-radius: 50%;
        }
    `;
};

export const headerDragStyles = (isDragging: boolean) => css`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;

    .agent__header {
        cursor: ${isDragging ? 'grabbing' : 'grab'};
        user-select: none;
    }
`;
